import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useRoutes,
  matchPath,
} from "react-router-dom";
import { navigateTo } from "./utils/navigationUtils";
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import { useDispatch, useSelector } from "react-redux";
import {
  changePopUpStatus,
  selectIsOpenModal,
  setRequestStatus,
} from "./redux/slices/popUp/popUpSlice";
import PopUpWindow from "./components/pop-up/PopUpWindow";
import { selectIsLoggedIn, setLoginData } from "./redux/slices/user/userSlice";
import {
  changeStatus,
  closeModal,
  openModal,
} from "./redux/slices/loginModal/modalSlice";
import { routes } from "./router/routes";
import { initGA, logPageView } from "./GoogleAnalytics/analytics";
import ComingSoonCountdown from "./pages/ComingSoonCountdown";
import i18n from "./i18n/i18n";
import { getlanguages } from "./axios/api/languagesApi";
import { getPendingService } from "./axios/api/PendingServiceApi";
import {
  selectPendingServiceData,
  setPendingServiceData,
} from "./redux/slices/pendingService/pendingServiceSlice";
import { refreshToken, verify, verifyForgetPassword } from "./axios/api/loginApi";

function App() {
  const dispatch = useDispatch();
  let location = useLocation();
  const navigate = useNavigate();
  const isOpenPopUp = useSelector(selectIsOpenModal);
  const [fontClass, setFontClass] = useState("font-roboto");
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const element = useRoutes(routes);

  // Initialize Google Analytics
  useEffect(() => {
    initGA(); // Initialize GA when app loads
  }, []);

  // Track page views on route change
  useEffect(() => {
    logPageView(); // Track the page view
  }, [location.pathname]); // Re-run logPageView when the path changes

  //  scroll behavior
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  useEffect(() => {
    const lastRefresh = localStorage.getItem("lastRefresh");
    const now = Date.now();

    const oneDay = 24 * 60 * 60 * 1000;

    if (!lastRefresh || now - lastRefresh > oneDay) {
      refreshToken();
      localStorage.setItem("lastRefresh", now.toString());
    }
  }, []);

  // check auth
  useEffect(() => {
    const storedUserData = JSON.parse(localStorage.getItem("userData"));
    const currentRoute = routes.find((route) =>
      matchPath(route.path, location.pathname),
    );
    // Check if route requires authentication
    if (currentRoute?.meta?.auth) {
      // If token is missing, redirect to login
      if (!storedUserData?.token) {
        navigateTo(navigate, "");
        dispatch(openModal(true));
        dispatch(changeStatus("SignIn"));
      }
    }
  }, [dispatch, location, navigate]);

  useEffect(() => {
    try {
      const storedUserData = localStorage.getItem("userData");
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        dispatch(setLoginData(parsedUserData));
      }
    } catch (error) {
      console.error("", error);
    }
  }, [dispatch]);

  useEffect(() => {
    const checkVerification = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const verifyToken = queryParams.get("verify");
        const forgotPassword = queryParams.get("forgotPassword");

        if (verifyToken) {
          const checkUser = await verify({ verify: verifyToken });

          // auto login
          if (checkUser?.status === true) {
            localStorage.setItem("userData", JSON.stringify(checkUser));
            dispatch(setRequestStatus(checkUser));
          }

          // open pop up
          dispatch(setLoginData(checkUser));
          dispatch(changePopUpStatus(true));

          // Remove the "verify" token from the URL
          // queryParams.delete("verify");
          // window.history.replaceState(
          //   null,
          //   "",
          //   queryParams.toString()
          //     ? "?" + queryParams.toString()
          //     : window.location.pathname
          // );
        } else if (forgotPassword) {
          const dataForget = await verifyForgetPassword({ verify: forgotPassword });
          if (dataForget?.status === true) {
            localStorage.setItem("token", forgotPassword);
            dispatch(changeStatus("ResetPassword"));
            navigateTo(navigate, `/login/`);
          }
        }
      } catch (error) {
        console.error("Verification error:", error);
      }
    };

    checkVerification();
  }, [location.search, dispatch]);

  // dynamic lang request
  useEffect(() => {
    const fetchTranslations = async () => {
      const res = await getlanguages();

      Object.entries(res).forEach(([lng, translations]) => {
        i18n.addResources(lng, "translation", translations);
      });
    };

    const lang = localStorage.getItem("i18nextLng");
    if (lang) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage("am");
      window.location.reload();
    }

    fetchTranslations();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const fetchPendingService = async () => {
        const res = await getPendingService();
        if (res.data) {
          dispatch(setPendingServiceData(res.data));
        }
      };

      fetchPendingService();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage === "am") {
      setFontClass("font-mardato");
    } else {
      setFontClass("font-roboto");
    }
  }, []);

  const [isDark, setIsDark] = useState(() => {
    return localStorage.getItem("theme") === "dark";
  });

  useEffect(() => {
    if (isDark) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  return (
    <div
      className={`App min-h-screen text-secondary_black dark:text-white transition-colors duration-500`}
    >
      <Header isDark={isDark} setIsDark={setIsDark} />
      {element}
      {isOpenPopUp && <PopUpWindow />}
      <Footer />
      {/* <ComingSoonCountdown /> */}
    </div>
  );
}

export default App;
